/**
 * Q5 — N2 · Competência 3/4 (Geometria e medidas) · task-model H1.19.K01
 *
 *   "Dada uma caixa desenhada sobre uma malha e uma animação de planificação controlada
 *    por um seletor, determinar o volume da caixa."
 *
 * ADAPTADO DA QUESTÃO 05 do material no GeoGebra: caixa em perspectiva sobre uma malha
 * de 2 cm por quadradinho, um seletor que abre a caixa e outro que escolhe o volume.
 * Aqui o volume é escolhido entre alternativas, e o seletor que resta é o que importa:
 * ele abre a caixa até a planificação deitar na malha, onde cada aresta pode ser
 * contada em quadradinhos.
 *
 * É N2, não N3: mexer no seletor é FERRAMENTA DE PENSAMENTO, não resposta. Com a caixa
 * fechada, a profundidade aparece encurtada pela perspectiva e não se conta; aberta, as
 * três medidas estão na malha, em tamanho verdadeiro.
 *
 * DUAS CONVERSÕES PARA ERRAR. Contar quadradinhos é metade do trabalho; a outra metade
 * é que cada quadradinho mede k cm, e o volume muda com o CUBO dessa escala. Os
 * distratores separam quem esqueceu a escala, quem a aplicou uma vez, quem usou o
 * fator de área — e quem leu mal a planificação.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { caixaPlanificando } from '../../render/caixa.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  VOL_ALTURA_COM_TAMPA,
  VOL_AREA_BASE,
  VOL_AREA_TOTAL,
  VOL_ESCALA_AO_QUADRADO,
  VOL_ESCALA_UMA_VEZ,
  VOL_PLANIFICACAO_COMO_ARESTA,
  VOL_SEM_ESCALA,
  VOL_SOMA_ARESTAS,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate, Maybe } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { br, strong, txt, visibleLength, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const CONTEXTOS: readonly string[] = [
  'Uma loja embala presentes em caixas de papelão com a forma de um paralelepípedo.',
  'Uma fábrica de calçados usa caixas com a forma de um paralelepípedo para embalar os pares de sapatos.',
  'Para guardar brinquedos, uma família comprou um baú com a forma de um paralelepípedo.',
  'Uma empresa de mudanças usa caixas de papelão com a forma de um paralelepípedo.',
];

export interface Params {
  /** Comprimento, largura e altura, em quadradinhos. */
  readonly a: number;
  readonly b: number;
  readonly c: number;
  /** Lado do quadradinho, em centímetros. */
  readonly k: number;
  readonly contexto: number;
}

const volume = (p: Params): number => p.a * p.b * p.c * p.k ** 3;

const f = (v: number): string => `${formatBR(v, 0)} cm³`;
const render = (v: number): RichText => [txt(f(v))];

/**
 * A régua do comprimento das alternativas é o erro mais simples, o que esquece a
 * escala: ele está sempre entre elas, e é o texto mais curto. Os demais — e a chave —
 * precisam caber até 1,6 vez esse comprimento.
 */
const teto = (p: Params): number => Math.floor(1.6 * visibleLength(render(p.a * p.b * p.c)));

function naFaixa(p: Params, v: number): Maybe<number> {
  if (!Number.isInteger(v) || v <= 0) return NOT_APPLICABLE;
  return visibleLength(render(v)) <= teto(p) ? v : NOT_APPLICABLE;
}

function drawParams(rng: Rng): Params {
  return sampleWhere(
    rng.fork('caixa'),
    (g) => ({
      a: g.int(2, 5),
      b: g.int(2, 4),
      c: g.int(2, 4),
      k: g.pick([2, 2, 3, 3, 5]),
      contexto: g.int(0, CONTEXTOS.length - 1),
    }),
    // Nem cubo (as três medidas iguais tornariam as leituras erradas indistinguíveis),
    // nem uma chave longa demais para caber ao lado do erro sem escala.
    (p) => !(p.a === p.b && p.b === p.c) && visibleLength(render(volume(p))) <= teto(p),
    { label: 'caixa', maxTries: 200 },
  );
}

const BUGGY: readonly BuggySolver<Params, number>[] = [
  // --- faixa 1: o erro que o item existe para revelar ------------------------
  { error: VOL_SEM_ESCALA, priority: 1, solve: (p) => naFaixa(p, p.a * p.b * p.c) },

  // --- faixa 2: sorteados entre si. Dois deles ficam ACIMA da chave, e é o que
  // impede a chave de ser sistematicamente a maior alternativa. -------------------
  {
    error: VOL_ESCALA_AO_QUADRADO,
    priority: 2,
    solve: (p) => naFaixa(p, p.a * p.b * p.c * p.k ** 2),
  },
  { error: VOL_ESCALA_UMA_VEZ, priority: 2, solve: (p) => naFaixa(p, p.a * p.b * p.c * p.k) },
  {
    error: VOL_AREA_TOTAL,
    priority: 2,
    solve: (p) => naFaixa(p, 2 * (p.a * p.b + p.b * p.c + p.c * p.a) * p.k ** 2),
  },
  { error: VOL_AREA_BASE, priority: 2, solve: (p) => naFaixa(p, p.a * p.b * p.k ** 2) },
  { error: VOL_SOMA_ARESTAS, priority: 2, solve: (p) => naFaixa(p, 4 * (p.a + p.b + p.c) * p.k) },
  {
    error: VOL_PLANIFICACAO_COMO_ARESTA,
    priority: 2,
    solve: (p) => naFaixa(p, (p.a + 2 * p.c) * p.b * p.c * p.k ** 3),
  },
  {
    error: VOL_ALTURA_COM_TAMPA,
    priority: 2,
    solve: (p) => naFaixa(p, p.a * p.b * (p.b + p.c) * p.k ** 3),
  },
];

const n0 = (x: number): string => formatBR(x, 0);

const EXPLICA: Explicacoes<Params, number> = {
  [VOL_SEM_ESCALA]: (p, v) => [
    txt(
      `Você chegou a ${f(v)} multiplicando as medidas contadas em quadradinhos: ${p.a} × ${p.b} × ${p.c} = ${n0(v)}. `,
    ),
    txt(
      `Esse é o número de cubinhos de ${p.k} cm de aresta que cabem na caixa, não o volume em cm³. ` +
        `Em centímetros, as arestas medem ${p.a * p.k}, ${p.b * p.k} e ${p.c * p.k}.`,
    ),
  ],
  [VOL_ESCALA_UMA_VEZ]: (p, v) => [
    txt(`Você fez ${p.a} × ${p.b} × ${p.c} × ${p.k} = ${f(v)}, convertendo a escala uma vez só. `),
    txt(
      `As três arestas estão em quadradinhos, e cada uma precisa ser multiplicada por ${p.k}: o fator do volume é ${p.k} × ${p.k} × ${p.k} = ${p.k ** 3}.`,
    ),
  ],
  [VOL_ESCALA_AO_QUADRADO]: (p, v) => [
    txt(`Você fez ${p.a * p.b * p.c} × ${p.k}² = ${f(v)}. `),
    txt(
      `Multiplicar por ${p.k}² = ${p.k ** 2} converte ÁREAS; para volumes, o fator é ${p.k}³ = ${p.k ** 3}.`,
    ),
  ],
  [VOL_AREA_TOTAL]: (p, v) => [
    txt(
      `Você chegou a ${f(v)} somando as áreas das seis faces: 2 × (${p.a * p.b} + ${p.b * p.c} + ${p.c * p.a}) quadradinhos, ` +
        `cada um com ${p.k ** 2} cm². `,
    ),
    txt(
      'Esse número mede a SUPERFÍCIE da caixa, em cm² — a área da planificação —, e não o espaço dentro dela.',
    ),
  ],
  [VOL_AREA_BASE]: (p, v) => [
    txt(`Você chegou a ${f(v)} calculando a área da base: ${p.a * p.k} cm × ${p.b * p.k} cm. `),
    txt(`Falta multiplicar pela altura, ${p.c * p.k} cm.`),
  ],
  [VOL_SOMA_ARESTAS]: (p, v) => [
    txt(
      `Você chegou a ${f(v)} somando as doze arestas: 4 × (${p.a * p.k} + ${p.b * p.k} + ${p.c * p.k}). `,
    ),
    txt('Essa soma, em centímetros, mede o contorno da caixa, não o espaço interno.'),
  ],
  [VOL_PLANIFICACAO_COMO_ARESTA]: (p, v) => [
    txt(
      `Você usou ${p.a + 2 * p.c} quadradinhos como comprimento: na planificação, essa medida é a base (${p.a}) ` +
        `somada às duas faces laterais (${p.c} + ${p.c}). `,
    ),
    txt(
      `O comprimento da caixa é ${p.a} quadradinhos. A conta feita foi ${(p.a + 2 * p.c) * p.k} × ${p.b * p.k} × ${p.c * p.k} = ${f(v)}.`,
    ),
  ],
  [VOL_ALTURA_COM_TAMPA]: (p, v) => [
    txt(
      `Você usou ${p.b + p.c} quadradinhos como altura, somando a face de trás (${p.c}) e a tampa (${p.b}), ` +
        'que ficam em sequência na planificação. ',
    ),
    txt(
      `A altura é só a face de trás: ${p.c} quadradinhos. A conta feita foi ${p.a * p.k} × ${p.b * p.k} × ${(p.b + p.c) * p.k} = ${f(v)}.`,
    ),
  ],
};

export const caixaPlanificacao: ItemTemplate<Params, number> = {
  id: templateId('h1-19-K01-caixa-planificacao'),
  // v2: câmera mais alta e a escala desenhada como o quadradinho do canto.
  // v3: a escala também escrita ao longo do lado que entra na profundidade.
  version: 3,
  taskModel: taskModelCode('H1.19.K01'),
  skills: [skillCode('H1.19'), skillCode('H1.25')],
  axis: 'II',
  level: 'N2',
  title: 'Volume de uma caixa pela planificação',
  responseModel: '3PL',

  difficultySources: ['steps.3plus', 'unit-conversion'],

  params: drawParams,
  solve: volume,
  buggy: comExplicacoes(BUGGY, EXPLICA),

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.k !== 2) fontes.push('non-round-numbers');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const interaction = assembleSelectOne<number>({
      structure: 'E1',
      key,
      distractors,
      render,
      raw: (v) => v,
      rng,
    });
    const d = { a: p.a, b: p.b, c: p.c };
    const desc =
      `Caixa com a forma de paralelepípedo, desenhada em perspectiva sobre uma malha quadriculada em que cada quadradinho ` +
      `tem ${p.k} cm de lado. Um seletor abre a caixa: as faces laterais giram até deitar na malha e a tampa se desdobra ` +
      `em sequência com a face de trás. Planificada, a base ocupa ${p.a} por ${p.b} quadradinhos, e as faces laterais ` +
      `têm ${p.c} quadradinhos de altura.`;
    const a11y = { title: 'Caixa sobre a malha quadriculada', desc };
    const legenda: RichText = [txt(`${p.k} cm`)];

    return {
      stem: [
        txt(CONTEXTOS[p.contexto] as string),
        br,
        txt('A figura mostra uma dessas caixas sobre uma malha quadriculada em que '),
        strong(`cada quadradinho tem ${p.k} cm de lado`),
        txt('. Com o seletor, a caixa se abre até ficar planificada sobre a malha.'),
      ],
      comando: [txt('Qual é o volume dessa caixa, em centímetros cúbicos?')],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'caixa',
          alt: desc,
          controls: [
            {
              id: 'abertura',
              label: [txt('Abrir a caixa')],
              min: 0,
              max: 100,
              step: 1,
              initial: 0,
              format: (v) => (v === 0 ? 'fechada' : v === 100 ? 'planificada' : `${n0(v)}%`),
            },
          ],
          frame: (valores) => caixaPlanificando(d, (valores['abertura'] ?? 0) / 100, legenda, a11y),
          // No papel não há seletor: vai a caixa já planificada, que é onde se conta.
          quadroImpresso: { abertura: 100 },
        },
      ],
      interaction,
      rationale: [
        strong('Resolução. '),
        txt(
          `Na planificação, conta-se: comprimento ${p.a} quadradinhos, largura ${p.b} e altura ${p.c}. ` +
            `Como cada quadradinho tem ${p.k} cm, as arestas medem ${p.a * p.k} cm, ${p.b * p.k} cm e ${p.c * p.k} cm.`,
        ),
        br,
        txt(`Volume = ${p.a * p.k} × ${p.b * p.k} × ${p.c * p.k} = ${f(key)}.`),
        br,
        txt(
          `Outro caminho: cabem ${p.a} × ${p.b} × ${p.c} = ${p.a * p.b * p.c} cubinhos de ${p.k} cm de aresta, ` +
            `cada um com ${p.k}³ = ${p.k ** 3} cm³: ${p.a * p.b * p.c} × ${p.k ** 3} = ${f(key)}.`,
        ),
      ],
      declared: p.k === 2 ? 'media' : 'dificil',
    };
  },
};
